import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Tooltip from "@mui/material/Tooltip";
import { MdFilterList, MdAddCircle } from "react-icons/md";
import loading from "../../assets/images/common/loading.gif";
import { Modal, Row, Col, Form, Button, OverlayTrigger } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import { MdEdit, MdDelete } from "react-icons/md";
import DataTable from "react-data-table-component";
import AppDataGrid from "../../components/common/AppDataGrid";

const SchoolMaster = () => {
  const [showFilterModal, setShowFilterModal] = useState(false);
  const handleCloseFilterModal = () => setShowFilterModal(false);
  const handleShowFilterModal = () => setShowFilterModal(true);
  const [schools, setSchools] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [form, setForm] = useState([]);
  const navigate = useNavigate();
  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const [filter, setFilter] = useState({
    school_name: "",
    branch: "",
    mobile_number: "",
    email: "",
    city: "",
    state: "",
    action: "FILTER",
  });

  const handleEditClick = (school_id) => {
    const schoolmasterToEdit = schools.find(
      (schools) => schools.school_id === school_id
    );
    if (schoolmasterToEdit) {
      navigate("/addNewSchool", {
        state: {
          schoolData: schoolmasterToEdit,
          imgName: schoolmasterToEdit.logo,
        },
      });
    } else {
      console.error(`School with ID ${school_id} not found.`);
    }
  };

  const fetchData = async (endpoint, setter) => {
    try {
      const response = await axios.post(baseUrl + endpoint, { action: "READ" });
      setter(response.data);
    } catch (error) {
      console.error(`Error fetching ${endpoint}:`, error);
    }
  };

  useEffect(() => {
    setIsLoading(true);
    fetchData("/schoolmaster/", setSchools).finally(() => setIsLoading(false));
  }, []);

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setForm((prevForm) => ({
      ...prevForm,
      [id]: value,
    }));
  };

  const handleDeleteClick = async (school_id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to change the status?"
    );
    if (!confirmDelete) return;

    try {
      const response = await axios.post(
        baseUrl + "/schoolmaster/",
        { school_id, action: "DELETE" },
        {
          headers: { "Content-Type": "application/json" },
        }
      );
      if (response.status < 200 || response.status >= 300) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      toast.success("Record Set to InActive");
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to delete record");
    }
  };

  const handleFilterSubmit = async (e) => {
    e.preventDefault();

    const formData = {
      school_name: filter.school_name || "",
      branch: filter.branch || "",
      city: filter.city || "",
      mobile_number: filter.mobile_number || "",
      email: filter.email || "",
      action: "FILTER",
    };
    const isAnyFieldFilled = Object.values(formData).some(
      (val) => val !== "" && val !== "FILTER"
    );
    if (isAnyFieldFilled) {
      try {
        const response = await axios.post(
          baseUrl + "/schoolmaster/",
          formData,
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
        const filterData = response.data || [];
        setSchools(filterData.length ? filterData : []);
        setShowFilterModal(false);
      } catch (error) {
        console.error("Error fetching data:", error);

        if (error.response && error.response.status === 404) {
          setSchools([]);
        } else {
          toast.error("Failed to fetch filtered data");
        }
      }
    } else {
      handleCloseFilterModal();
      fetchData("/schoolmaster/", setSchools);
    }
  };

  const handleFilterClear = async () => {
    setFilter({
      school_id: 0,
      school_name: "",
      branch: "",
      mobile_number: "",
      city: "",
      action: "FILTER",
    });
    setForm({ ...form, email: "" });
    fetchData("/schoolmaster/", setSchools);
  };

  const columns = [
    {
      field: "school_name",
      headerName: "School Name",
      flex: 1,
      sortable: true,
      renderCell: (params) => {
        const name = params.value || "-";
        return (
          <Tooltip title={name}>
            <span>{params.value}</span>
          </Tooltip>
        );
      },
    },
    {
      field: "branch",
      headerName: "Branch",
      flex: 1,
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={params.value || ""}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "school_location",
      headerName: "Location",
      flex: 1,
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={params.value || ""}>
          <span>
            {params.value && params.value.length > 10
              ? params.value.substring(0, 10) + "..."
              : params.value || ""}
          </span>
        </Tooltip>
      ),
    },
    {
      field: "city",
      headerName: "City",
      flex: 1,
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={params.value || ""}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "mobile_number",
      headerName: "Mobile Number",
      flex: 1,
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={params.value || ""}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={params.value || ""}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={params.value || ""}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      sortable: false,
      renderCell: (params) =>
        params.row.school_id !== "No records found" ? (
          <div className="tableActions">
            <Tooltip title="Edit" arrow>
              <span
                className="commonActionIcons"
                onClick={() => handleEditClick(params.row.school_id)}
              >
                <MdEdit />
              </span>
            </Tooltip>
            <Tooltip title="Delete" arrow>
              <span
                className="commonActionIcons"
                onClick={() => handleDeleteClick(params.row.school_id)}
              >
                <MdDelete />
              </span>
            </Tooltip>
          </div>
        ) : null,
    },
  ];

  const searchableColumns = [
    (row) => row.school_name,
    (row) => row.city,
    (row) => row.branch,
    (row) => row.mobile_number,
    (row) => row.email,
    (row) => row.school_location,
    (row) => row.status,
  ];

  const trimmedQuery = searchQuery.trim().toLowerCase();

  const filteredRecords =
    (trimmedQuery || "").length > 0
      ? (schools || []).filter((school) =>
        searchableColumns.some((selector) => {
          const value = selector(school);
          return String(value || "")
            .toLowerCase()
            .includes(trimmedQuery.toLowerCase());
        })
      )
      : schools || [];

  const handleSearchChange = (event) => {
    // refresh data and update search text
    fetchData("/schoolmaster/", setSchools);
    setSearchQuery(event.target.value);
  };

  return (
    <div className="pageBody">
      <ToastContainer />

      <div className="commonDataTableHead">
        <div className="d-flex justify-content-between align-items-center w-100">
          <div className="d-flex align-items-center" style={{ gap: "10px" }}>
            <h6 className="commonTableTitle">School Master</h6>
          </div>
          <div className="">
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              className="searchInput"
              onChange={handleSearchChange}
            />
          </div>
          <div className="d-flex align-items-center" style={{ gap: 6 }}>
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="tooltip-top">Filter</Tooltip>}
            >
              <Button
                className="secondaryBtn"
                variant="secondary"
                onClick={handleShowFilterModal}
              >
                <MdFilterList />
              </Button>
            </OverlayTrigger>

            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="tooltip-top">Add</Tooltip>}
            >
              <Button
                className="primaryBtn"
                variant="primary"
                onClick={() => navigate("/addNewSchool")}
              >
                <MdAddCircle />
              </Button>
            </OverlayTrigger>
          </div>
        </div>
      </div>

      <div className="commonTable height100">
        <div className="tableBody">
          {isLoading ? (
            <div className="loadingContainer">
              <img src={loading} alt="Loading..." className="loadingGif" />
            </div>
          ) : (
            <AppDataGrid
              // IMPORTANT: don't inject a fake "No records found" row;
              // let DataGrid show its built-in "No rows" message.
              rows={Array.isArray(filteredRecords) ? filteredRecords : []}
              columns={columns}
              getRowId={(row) => row.school_id || Math.random()}
              showToolbar={false}
              pagination={
                Array.isArray(filteredRecords) && filteredRecords.length > 0
              }
              pageSizeOptions={[10, 20, 50]}
              density="compact"
              sx={{
                maxHeight: 600,
              }}
            />
          )}
        </div>
      </div>

      <Modal
        show={showFilterModal}
        onHide={handleCloseFilterModal}
        className="commonFilterModal"
      >
        <Modal.Header closeButton className="modalHeaderFixed">
          <Modal.Title>Filter</Modal.Title>
        </Modal.Header>
        <Modal.Body className="modalBodyScrollable">
          <Form id="filterForm" onSubmit={handleFilterSubmit}>
            <Row>
              <Col xs={12}>
                <div className="commonInput">
                  <Form.Group controlId="school_name">
                    <Form.Label>School Name</Form.Label>
                    <Form.Control
                      type="text"
                      id="school_name"
                      name="school_name"
                      placeholder="School Name"
                      value={filter.school_name || ""}
                      maxLength={30}
                      onChange={(e) => {
                        const value = e.target.value.replace(
                          /[^A-Za-z\s]/g,
                          ""
                        );
                        setFilter({ ...filter, school_name: value });
                      }}
                    />
                  </Form.Group>
                </div>
              </Col>

              <Col xs={12}>
                <div className="commonInput">
                  <Form.Group controlId="branch">
                    <Form.Label>Branch</Form.Label>
                    <Form.Control
                      type="text"
                      id="branch"
                      name="branch"
                      placeholder="Branch"
                      maxLength={50}
                      value={filter.branch || ""}
                      onChange={(e) =>
                        setFilter({ ...filter, branch: e.target.value.trim() })
                      }
                    />
                  </Form.Group>
                </div>
              </Col>
              <Col xs={12}>
                <div className="commonInput">
                  <Form.Group controlId="mobile_number">
                    <Form.Label>Mobile Number</Form.Label>
                    <Form.Control
                      type="text"
                      name="mobile_number"
                      id="mobile_number"
                      placeholder="Enter Mobile Number"
                      value={filter.mobile_number || ""}
                      maxLength={10}
                      onChange={(e) => {
                        const value = e.target.value.trim().replace(/\D/g, "");
                        setFilter({
                          ...filter,
                          mobile_number: value,
                        });
                      }}
                    />
                  </Form.Group>
                </div>
              </Col>
              <Col xs={12}>
                <div className="commonInput">
                  <Form.Group>
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      required
                      type="email"
                      id="email"
                      value={form.email}
                      placeholder="Enter Email Address"
                      maxLength={150}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </div>
              </Col>
              <Col xs={12}>
                <div className="commonInput">
                  <Form.Group controlId="city">
                    <Form.Label>City</Form.Label>
                    <Form.Control
                      type="text"
                      name="city"
                      id="city"
                      placeholder="Enter City"
                      maxLength={50}
                      value={filter.city || ""}
                      onChange={(e) => {
                        const value = e.target.value.replace(
                          /[^A-Za-z\s]/g,
                          ""
                        );
                        setFilter({ ...filter, city: value });
                      }}
                    />
                  </Form.Group>
                </div>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer className="modalFooterFixed">
          <div className="">
            <Button
              variant="secondary"
              className="btn-info clearBtn"
              onClick={handleFilterClear}
            >
              Reset
            </Button>
          </div>
          <div className="">
            <Button
              variant="secondary"
              className=" btn-danger secondaryBtn"
              onClick={() => {
                handleCloseFilterModal();
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              className="btn-success primaryBtn"
              onClick={handleFilterSubmit}
            >
              Search
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
export default SchoolMaster;
